import { useCallback, useEffect, useMemo, useState } from 'react';

import { ClientsTable, StatusFilter } from '../../components/AIPrompts';
import { SearchFilter } from '../../components/CallLog/SearchFilter';
import { AIPromptModal } from '../../components/Modals';
import { Button } from '../../components/Shared';
import { AI_PROMPT_STATUS_FILTER } from '../../constants';
import { Layout } from '../../layouts';
import { getAIPromptClients } from '../../services/aiPromptsApi';

const INITIAL_FILTERS = {
  search: '',
  status: AI_PROMPT_STATUS_FILTER.ALL,
};

const isStatusMatched = ({ precisionConversation }, status) => {
  if (status === AI_PROMPT_STATUS_FILTER.ON) {
    return precisionConversation.isEnabled;
  }
  if (status === AI_PROMPT_STATUS_FILTER.OFF) {
    return !precisionConversation.isEnabled;
  }
  return true;
};

export const AIPrompts = () => {
  const [clients, setClients] = useState([]);
  const [isClientsLoading, setIsClientsLoading] = useState(true);
  const [isClientsError, setIsClientsError] = useState(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [promptModal, setPromptModal] = useState(null);

  const fetchClients = useCallback(async () => {
    setIsClientsLoading(true);
    setIsClientsError(false);

    try {
      setClients(await getAIPromptClients());
    } catch {
      setIsClientsError(true);
    } finally {
      setIsClientsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const filteredClients = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return clients.filter(
      client =>
        client.tenantName.toLowerCase().includes(search) &&
        isStatusMatched(client, filters.status),
    );
  }, [clients, filters]);

  const numberClientsOn = clients.filter(
    ({ precisionConversation }) => precisionConversation.isEnabled,
  ).length;
  const isFiltersChanged =
    filters.search !== INITIAL_FILTERS.search ||
    filters.status !== INITIAL_FILTERS.status;

  const onOpenPrompt = (client, feature, shouldTurnOn) => {
    setPromptModal({ tenantId: client.tenantId, feature, shouldTurnOn });
  };

  const onChangeStatus = (tenantId, featureKey, status) => {
    setClients(prev =>
      prev.map(client =>
        client.tenantId === tenantId
          ? { ...client, [featureKey]: status }
          : client,
      ),
    );
  };

  const promptModalClient =
    promptModal &&
    clients.find(({ tenantId }) => tenantId === promptModal.tenantId);

  return (
    <Layout>
      <div className="flex flex-col gap-4 h-full overflow-hidden">
        <div className="flex items-center justify-between gap-3 shrink-0">
          <h1 className="text-base-1000 text-[22px]/[28px] font-semibold py-2.5">
            AI Prompts
          </h1>
          {!isClientsLoading && !isClientsError && (
            <span className="text-base-800 text-[14px]/[20px]">
              Precision Conversation is on for {numberClientsOn} of{' '}
              {clients.length} clients
            </span>
          )}
        </div>
        <div className="flex gap-3 overflow-auto shrink-0">
          <SearchFilter
            key={filters.search}
            search={filters.search}
            onChangeSearch={setFilters}
          />
          <StatusFilter status={filters.status} onChangeStatus={setFilters} />
          {isFiltersChanged && (
            <Button
              title="Clear All"
              variant="text"
              onClick={() => setFilters(INITIAL_FILTERS)}
              className="!bg-transparent hover:!bg-transparent focus:!bg-transparent text-brand-900 p-2"
            />
          )}
        </div>
        <ClientsTable
          clients={filteredClients}
          isClientsLoading={isClientsLoading}
          isClientsError={isClientsError}
          fetchClients={fetchClients}
          onOpenPrompt={onOpenPrompt}
          onChangeStatus={onChangeStatus}
        />
        {promptModalClient && (
          <AIPromptModal
            client={promptModalClient}
            feature={promptModal.feature}
            shouldTurnOn={promptModal.shouldTurnOn}
            onClose={() => setPromptModal(null)}
            onChangeStatus={onChangeStatus}
          />
        )}
      </div>
    </Layout>
  );
};
